import { useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { ArrowUpRight, CheckCircle2, Download, Loader2, Mail } from "lucide-react";
import Header from "@/components/site/Header";
import Footer from "@/components/site/Footer";
import SEO from "@/components/seo/SEO";

const API = process.env.REACT_APP_BACKEND_URL;
const MAX_POLLS = 10;

const Welcome = () => {
  const [params] = useSearchParams();
  const orderId = params.get("order_id");
  const isOrder = params.get("type") === "order";
  const hasClaim = !!(params.get("checkout_id") && params.get("signature"));
  const [claim, setClaim] = useState(hasClaim ? { status: "loading" } : null);
  const attempts = useRef(0);

  useEffect(() => {
    if (!hasClaim) return undefined;
    let stopped = false;
    const poll = async () => {
      try {
        const res = await fetch(
          `${API}/api/checkout/claim${window.location.search}`
        );
        if (!res.ok) throw new Error("claim_failed");
        const data = await res.json();
        if (stopped) return;
        if (data.status === "ready") {
          setClaim(data);
          return;
        }
        attempts.current += 1;
        if (attempts.current >= MAX_POLLS) {
          setClaim({ status: "failed" });
          return;
        }
        setTimeout(poll, 3000);
      } catch {
        if (!stopped) setClaim({ status: "failed" });
      }
    };
    poll();
    return () => {
      stopped = true;
    };
  }, [hasClaim]);

  const isDownloadReady = claim?.status === "ready" && claim.type === "download";
  const isMembership = claim?.status === "ready" && claim.type === "membership";
  const isPreparing = isOrder && claim?.status === "loading";

  return (
    <main data-testid="welcome-page" className="min-h-screen bg-[#F7F7F4] flex flex-col">
      <SEO title="Welcome to Vaymilo" description="Your purchase is complete." path="/welcome" noindex />
      <Header />

      <section className="flex-1">
        <div className="max-w-3xl mx-auto px-6 md:px-10 py-20 md:py-32">
          <div className="inline-flex items-center gap-2 bg-[#2563EB]/10 text-[#2563EB] border border-[#2563EB]/20 rounded-full px-4 py-1.5 font-mono text-[11px] tracking-[0.18em] uppercase">
            <CheckCircle2 size={13} strokeWidth={2.5} />
            Payment successful
          </div>

          <h1
            data-testid="welcome-headline"
            className="mt-7 font-sora text-[40px] sm:text-[54px] lg:text-[64px] font-extrabold tracking-tight leading-[1.03] text-[#111111]"
          >
            {isOrder ? (
              <>
                It&rsquo;s yours.
                <br />
                {isDownloadReady ? "Grab your files." : "Check your inbox."}
              </>
            ) : (
              <>
                You&rsquo;re in.
                <br />
                Welcome to Vaymilo{isMembership ? ` ${claim.tier_label}` : ""}.
              </>
            )}
          </h1>

          <p className="mt-6 max-w-xl font-manrope text-[16px] md:text-[18px] leading-[1.7] text-[#111111]/70">
            {isOrder
              ? isDownloadReady
                ? `Your ${claim.product_name} is ready right now — download it below. We've also emailed the link to the address you used at checkout as a backup.`
                : "Your payment went through. We've emailed your download link to the address you used at checkout — it works 3 times and stays valid for 72 hours."
              : "Your membership is now active. We've sent a welcome email to the address you used at checkout — it has everything you need to get started."}
          </p>

          {/* Unique download card — appears when the claim resolves */}
          {isOrder && (isDownloadReady || isPreparing) && (
            <div
              data-testid="welcome-download-card"
              className="mt-10 bg-[#111111] rounded-2xl p-7 md:p-8"
            >
              {isPreparing ? (
                <div className="flex items-center gap-4">
                  <Loader2 size={20} className="animate-spin text-[#2563EB]" />
                  <div>
                    <div className="font-sora text-[17px] font-bold text-white">
                      Preparing your download…
                    </div>
                    <p className="mt-1 font-manrope text-[14px] text-white/60">
                      This usually takes a few seconds.
                    </p>
                  </div>
                </div>
              ) : (
                <>
                  <div className="font-mono text-[10px] tracking-[0.22em] uppercase text-white/50">
                    Your unique download link
                  </div>
                  <div className="mt-3 font-sora text-[22px] md:text-[26px] font-bold text-white tracking-tight">
                    {claim.product_name}
                  </div>
                  <a
                    href={`${API}${claim.download_url}`}
                    data-testid="welcome-download-btn"
                    className="mt-6 inline-flex items-center gap-2.5 bg-[#2563EB] hover:bg-[#1D4ED8] text-white font-manrope font-semibold text-[15px] px-6 py-3.5 rounded-lg transition-colors"
                  >
                    <Download size={16} strokeWidth={2.25} />
                    Download your files (ZIP)
                  </a>
                  <p className="mt-4 font-manrope text-[12.5px] text-white/50">
                    This link is unique to your purchase — it works{" "}
                    {claim.max_uses - (claim.uses || 0)} more{" "}
                    {claim.max_uses - (claim.uses || 0) === 1 ? "time" : "times"} and
                    expires in 72 hours. A backup copy is in your email.
                  </p>
                </>
              )}
            </div>
          )}

          <div className="mt-10 bg-white border border-[#E7E7E2] rounded-2xl p-7 md:p-8">
            <div className="flex items-start gap-4">
              <div className="shrink-0 w-10 h-10 rounded-lg bg-[#2563EB]/10 flex items-center justify-center">
                <Mail size={17} className="text-[#2563EB]" />
              </div>
              <div>
                <div className="font-sora text-[17px] font-bold text-[#111111]">
                  {isOrder ? "Didn't get the email?" : "Sign in with your email"}
                </div>
                <p className="mt-2 font-manrope text-[14.5px] leading-relaxed text-[#111111]/65">
                  {isOrder
                    ? "Give it a minute and check spam. Still nothing? Write to hello@vaymilo.com with your order ref and we'll re-send your link within 24 hours."
                    : "No password needed. Enter the same email you used at checkout and we'll send you a magic sign-in link — your tier and library access will be waiting."}
                </p>
              </div>
            </div>
            <div className="mt-6 flex flex-wrap gap-3">
              {isOrder ? (
                <>
                  <Link
                    to="/products"
                    data-testid="welcome-products-btn"
                    className="inline-flex items-center gap-2 bg-[#2563EB] hover:bg-[#1D4ED8] text-white font-manrope font-medium text-[14.5px] px-5 py-3 rounded-lg transition-colors"
                  >
                    Browse more kits
                    <ArrowUpRight size={14} strokeWidth={2.25} />
                  </Link>
                  <Link
                    to="/pricing"
                    data-testid="welcome-membership-btn"
                    className="inline-flex items-center gap-2 bg-white text-[#111111] font-manrope font-medium text-[14.5px] px-5 py-3 rounded-lg border border-[#E7E7E2] hover:border-[#111111] transition-colors"
                  >
                    Get every kit with Studio
                  </Link>
                </>
              ) : (
                <>
                  <Link
                    to="/login"
                    data-testid="welcome-signin-btn"
                    className="inline-flex items-center gap-2 bg-[#2563EB] hover:bg-[#1D4ED8] text-white font-manrope font-medium text-[14.5px] px-5 py-3 rounded-lg transition-colors"
                  >
                    Sign in to your account
                    <ArrowUpRight size={14} strokeWidth={2.25} />
                  </Link>
                  <Link
                    to="/library"
                    data-testid="welcome-library-btn"
                    className="inline-flex items-center gap-2 bg-white text-[#111111] font-manrope font-medium text-[14.5px] px-5 py-3 rounded-lg border border-[#E7E7E2] hover:border-[#111111] transition-colors"
                  >
                    Browse the library
                  </Link>
                </>
              )}
            </div>
          </div>

          {orderId && (
            <p className="mt-6 font-mono text-[11px] tracking-[0.14em] uppercase text-[#111111]/40">
              Order ref · {orderId}
            </p>
          )}
        </div>
      </section>

      <Footer />
    </main>
  );
};

export default Welcome;
